import { useParams, useLocation } from 'react-router-dom';
import NotFound from './not-found';
import CompoundInterestCalculator from '../components/calculators/CompoundInterestCalculator';
import NetSalaryCalculator from '../components/calculators/NetSalaryCalculator';
import MonthlySavingsCalculator from '../components/calculators/MonthlySavingsCalculator';
import MortgageCalculator from '../components/calculators/MortgageCalculator'; // Import the new calculator
import SalaryComparisonCalculator from '../components/calculators/SalaryComparisonCalculator';
import { useI18n } from '../i18n/i18n';
import { useToolSEO } from '../hooks/useSEO';
import StructuredData from '../components/StructuredData';
import { CALCULATOR_SCHEMAS, createBreadcrumbSchema } from '../lib/schema-factory';

// Tool detail page that renders the appropriate calculator based on the URL parameter
const ToolDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { t } = useI18n();

  // Map tool IDs to their components and titles
  const toolComponents: Record<string, {
    component: React.ComponentType,
    title: string,
    description: string
  }> = {
    'compound-interest': {
      component: CompoundInterestCalculator,
      title: t('tools.compound.title'),
      description: t('tools.compound.description')
    },
    'net-salary': {
      component: NetSalaryCalculator,
      title: t('tools.salary.title'),
      description: t('tools.salary.description')
    },
    'monthly-savings-calculator': {
      component: MonthlySavingsCalculator,
      title: t('tools.savings.title'),
      description: t('tools.savings.description')
    },
    'mortgage-calculator': {
      component: MortgageCalculator,
      title: t('tools.mortgage.title'),
      description: t('tools.mortgage.description')
    },
    'salary-comparison': {
      component: SalaryComparisonCalculator,
      title: t('tools.salaryComparison.title'),
      description: t('tools.salaryComparison.description')
    }
  };

  // Get the tool info
  const toolInfo = id ? toolComponents[id] : undefined;

  // If the tool doesn't exist, show 404
  if (!toolInfo) {
    return <NotFound />;
  }

  // SEO Configuration using centralized metadata
  // Falls back to configured metadata from seo.config.ts if tool ID matches
  const seoContent = useToolSEO(id || '');

  // Render the corresponding calculator component
  const ToolComponent = toolInfo.component;

  // Get the appropriate schema for this calculator
  const getCalculatorSchema = () => {
    switch(id) {
      case 'compound-interest':
        return CALCULATOR_SCHEMAS.compoundInterest();
      case 'net-salary':
        return CALCULATOR_SCHEMAS.salary();
      case 'monthly-savings-calculator':
        return CALCULATOR_SCHEMAS.monthlySavings?.() || CALCULATOR_SCHEMAS.compoundInterest(); // Fallback
      case 'mortgage-calculator':
        return CALCULATOR_SCHEMAS.mortgage();
      case 'salary-comparison':
        return CALCULATOR_SCHEMAS.salary(); // Reuse salary schema
      default:
        return null;
    }
  };

  const calculatorSchema = getCalculatorSchema();

  // Generate breadcrumb schema based on current path
  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Tools', url: '/tools' },
    { name: toolInfo.title, url: location.pathname }
  ]);

  return (
    <section className="bg-white py-16 fade-in">
      {seoContent}
      {/* Calculator-specific structured data */}
      {calculatorSchema && <StructuredData schema={calculatorSchema} />}
      
      {/* Breadcrumb structured data */}
      <StructuredData schema={breadcrumbSchema} />
      
      <ToolComponent />

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .fade-in {
            animation: fadeIn 0.5s ease forwards;
          }
        `}
      </style>
    </section>
  );
};

export default ToolDetailPage;